import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articleMap, getRelatedArticles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleMap[slug] : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">文章不存在</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categoryMap[article.category];
  const relatedArticles = getRelatedArticles(article);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    inLanguage: 'zh-CN',
    url: `https://jiahe01.cn/article/${article.slug}`,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://jiahe01.cn',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: category?.name || article.category,
        item: `https://jiahe01.cn/category/${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(category ? [{ label: category.name, href: `/category/${article.category}` }] : []),
              { label: article.title },
            ]}
          />

          <article className="py-8 md:py-12">
            <header className="mb-10">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                {category && (
                  <Link
                    to={`/category/${article.category}`}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-800 hover:bg-accent-200 transition-colors duration-200 whitespace-nowrap"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-sm text-foreground-500 whitespace-nowrap">{article.publishDate}</span>
                <span className="text-sm text-foreground-500 whitespace-nowrap">{article.readTime} 分钟阅读</span>
                <span className="text-sm text-foreground-500 whitespace-nowrap">作者：{article.author}</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-2.5 py-1 rounded whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>
            </header>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} - 援助交配现象研究`}
                className="w-full h-64 md:h-96 object-cover object-top"
              />
            </div>

            <div
              className="article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 font-heading font-bold text-sm">
                    {article.author.charAt(0)}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                    <p className="text-xs text-foreground-500">社会学研究员</p>
                  </div>
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="pb-16 md:pb-20">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.slug} article={ra} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}